/**
 * Boxes as printed on the Coast Guard forms that have no planning card of
 * their own, taken from the Jan-2026 pack PDFs.
 *
 * Each box names the field on the exporter's own form inputs, so a card shows
 * exactly what the PDF will say. The 211 CAT column is `resourceCategory`.
 */
import type { IcsDocumentBox } from './IcsDocumentCard';

/* ICS 207-CG Incident Organization Chart */

export const ICS207_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Incident Location:', field: 'incidentLocation' },
  {
    kind: 'fields',
    label: '3. Operational Period (Date/Time):',
    fields: [
      { field: 'opFrom', label: 'Date/Time' },
      { field: 'opTo', label: 'To' },
    ],
  },
  /*
   * Box 4 is a chart, not a list. The exporter can fall back to printing one
   * name per position box, but what the sheet is meant to show is the
   * organisation and its reporting lines, so the card renders the roster's own
   * chart instead.
   */
  {
    kind: 'slot',
    label: '4. Incident Organization Chart',
    slot: 'orgChart',
  },
  {
    kind: 'fields',
    label: '5. Prepared By',
    fields: [
      { field: 'preparedByName', label: 'Name' },
      { field: 'preparedByPosition', label: 'Position Title' },
      { field: 'preparedDateTime', label: 'Date/Time' },
    ],
  },
];

/* ICS 211-CG Incident Check-in List */

export const ICS211_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Incident Location:', field: 'incidentLocation' },
  {
    kind: 'fields',
    label: '3. Operational Period (Date/Time):',
    fields: [
      { field: 'opFrom', label: 'From' },
      { field: 'opTo', label: 'To' },
    ],
  },
  {
    kind: 'parallel',
    label: 'Check-in Information',
    columns: [
      { field: 'checkinDateTime', label: '4. Date/Time Check-in' },
      { field: 'stateAndAgency', label: '5. State and Agency' },
      { field: 'resourceCategory', label: 'CAT' },
      { field: 'resourceKind', label: 'Kind' },
      { field: 'resourceType', label: 'Type' },
      { field: 'resourceNameIdentifier', label: 'Resource Name or Identifier' },
      { field: 'strikeTeamTaskForce', label: 'ST / TF' },
      { field: 'orderRequestNumber', label: '6. Order Request Number' },
      { field: 'leaderName', label: '7. Leader’s Name' },
      { field: 'personnelCount', label: '8. Total # of Personnel' },
      { field: 'incidentContactInfo', label: '9. Incident Contact Information' },
      { field: 'homeUnitAgency', label: '10. Home (Base) Unit or Agency' },
      { field: 'departurePointDateTime', label: '11. Departure Point Date/Time' },
      { field: 'methodOfTravel', label: '12. Method of Travel' },
      {
        field: 'incidentAssignmentLocation',
        label: '13. Incident Assignment (Location)',
      },
      { field: 'otherQualifications', label: '14. Other Qualifications' },
      { field: 'dataSentToResl', label: '15. Data sent to RESL' },
    ],
  },
  {
    kind: 'fields',
    label: '16. Prepared By:',
    fields: [
      { field: 'preparedBy', label: 'Name' },
      { field: 'preparedByPosition', label: 'Position Title' },
    ],
  },
];

/* ICS 211A-CG Daily Personnel Sign-In Sheet */

export const ICS211A_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Incident Location:', field: 'incidentLocation' },
  { kind: 'value', label: '3. Sign-in Location:', field: 'signinLocation' },
  {
    kind: 'fields',
    label: '4. Operational Period (Date/Time):',
    fields: [
      { field: 'opFrom', label: 'From' },
      { field: 'opTo', label: 'To' },
    ],
  },
  {
    kind: 'rows',
    label: 'Personnel Sign-In Information',
    field: 'entries',
    columns: [
      { field: 'date', label: '5. Date' },
      { field: 'time_in', label: '6. Time In' },
      { field: 'name', label: '7. Name' },
      { field: 'organization', label: '8. Organization' },
      { field: 'ics_assignment', label: '9. ICS Assignment' },
      { field: 'contact_information', label: '10. Contact Information' },
      { field: 'initial_checkin_complete', label: '11. Initial Check-In Complete' },
      { field: 'time_out', label: '12. Time Out' },
    ],
  },
  {
    kind: 'fields',
    label: '13. Recorded By:',
    fields: [
      { field: 'preparedBy', label: 'Name' },
      { field: 'positionTitle', label: 'Position Title' },
    ],
  },
];

/* ICS 213RR-CG Resource Request */

export const ICS213RR_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Date/Time:', field: 'opFrom' },
  {
    kind: 'value',
    label: '3. Resource Request Number:',
    field: 'resourceRequestNumber',
  },
  {
    kind: 'parallel',
    label: '4. Order:',
    columns: [
      { field: 'quantities', label: 'a. Qty' },
      { field: 'kinds', label: 'b. Kind' },
      { field: 'types', label: 'c. Type' },
      { field: 'priorities', label: 'd. Priority' },
      { field: 'descriptions', label: 'e. Detailed Item Description' },
      { field: 'locations', label: 'f. Requested Reporting Location' },
      { field: 'dateTimes', label: 'f. Date/Time' },
      { field: 'orderNumbers', label: 'g. Order # (LSC)' },
      { field: 'etas', label: 'h. ETA (LSC)' },
      { field: 'costs', label: 'i. Cost (LSC)' },
    ],
  },
  {
    kind: 'value',
    label: '5. Suggested Source(s) of Supply and Suitable Substitutes:',
    field: 'supplySources',
  },
  {
    kind: 'fields',
    label: '6. Requested by:',
    fields: [
      { field: 'requestorName', label: 'Name' },
      { field: 'requestorDateTime', label: 'Date/Time' },
    ],
  },
  {
    kind: 'fields',
    label: '7. Section Chief / Command Staff Approval:',
    fields: [
      { field: 'sectionChiefName', label: 'Name' },
      { field: 'sectionChiefPosition', label: 'Position' },
      { field: 'sectionChiefDateTime', label: 'Date/Time' },
      { field: 'sectionChiefComments', label: 'Comments' },
    ],
  },
  {
    kind: 'fields',
    label: '8. RESL:',
    fields: [
      { field: 'reslTac', label: 'a. Tactical' },
      { field: 'reslPersonnel', label: 'a. Personnel' },
      { field: 'reslAvailable', label: 'b. Available' },
      { field: 'reslNotAvailable', label: 'c. Not available' },
    ],
  },
  {
    kind: 'fields',
    label: '9. RESL Review/Signature:',
    fields: [
      { field: 'reslName', label: 'Name' },
      { field: 'reslDateTime', label: 'Date/Time' },
      { field: 'reslComments', label: 'Comments' },
    ],
  },
  {
    kind: 'value',
    label: '10. Requisition/Purchase Order #:',
    field: 'logisticsOrderNumber',
  },
  {
    kind: 'value',
    label: '11. Supplier (Name/Phone/Fax/Email):',
    field: 'supplierInfo',
  },
  { kind: 'value', label: '12. Notes:', field: 'logisticsNotes' },
  {
    kind: 'fields',
    label: '13. Logistics Section Approval:',
    fields: [
      { field: 'logisticsName', label: 'Name' },
      { field: 'logisticsPosition', label: 'Position' },
      { field: 'logisticsDateTime', label: 'Date/Time' },
    ],
  },
  {
    kind: 'fields',
    label: '14. Order placed by:',
    fields: [
      { field: 'logisticsOrderPlacedBy', label: 'Position' },
      { field: 'logisticsOrderPlacedByOther', label: 'Other' },
    ],
  },
  {
    kind: 'value',
    label: '15. Reply / Comments from Finance:',
    field: 'financeComments',
  },
  {
    kind: 'fields',
    label: '16. Finance Section Approval:',
    fields: [
      { field: 'financeName', label: 'Name' },
      { field: 'financePosition', label: 'Position' },
      { field: 'financeDateTime', label: 'Date/Time' },
    ],
  },
];

/* ICS 214-CG Activity Log */

export const ICS214_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  {
    kind: 'fields',
    label: '2. Operational Period:',
    fields: [
      { field: 'opFrom', label: 'Date/Time from' },
      { field: 'opTo', label: 'Date/Time to' },
    ],
  },
  { kind: 'value', label: '3. Member Name:', field: 'memberName' },
  { kind: 'value', label: '4. ICS Position:', field: 'icsPosition' },
  { kind: 'value', label: '5. Home Agency (and Unit):', field: 'homeAgency' },
  {
    kind: 'parallel',
    label: '6. Resources Assigned:',
    columns: [
      { field: 'rosterNameRows', label: 'Name' },
      { field: 'rosterPositionRows', label: 'ICS Position' },
      { field: 'rosterHomeAgencyRows', label: 'Home Agency (and Unit)' },
    ],
  },
  {
    kind: 'parallel',
    label: '7. Activity Log:',
    columns: [
      { field: 'activityDateTimes', label: 'Date / Time' },
      { field: 'activityDescriptions', label: 'Notable Activities' },
    ],
  },
  {
    kind: 'fields',
    label: '8. Prepared by:',
    fields: [
      { field: 'preparedByName', label: 'Name' },
      { field: 'preparedByPosition', label: 'Position Title' },
    ],
  },
];

/* ICS 221-CG Demobilization Check-Out */

export const ICS221_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Incident Location:', field: 'incidentLocation' },
  {
    kind: 'fields',
    label: '3. Planned Release Date:',
    fields: [
      { field: 'plannedReleaseDate', label: 'Date' },
      { field: 'plannedReleaseTime', label: 'Time' },
    ],
  },
  {
    kind: 'value',
    label: '4. Resource / Personnel Released:',
    field: 'resourcePersonnelReleased',
  },
  {
    kind: 'value',
    label: '5. Order Request Number:',
    field: 'orderRequestNumber',
  },
  {
    kind: 'rows',
    label: '6. Release of Resource or Personnel:',
    field: 'checkoffRows',
    columns: [
      { field: 'unit', label: 'Unit / Manager' },
      { field: 'checked', label: 'Required' },
      { field: 'remarks', label: 'Remarks' },
      { field: 'name', label: 'Name' },
      { field: 'signedAt', label: 'Signature' },
    ],
  },
  { kind: 'value', label: '7. Remarks:', field: 'remarks' },
  {
    kind: 'fields',
    label: '8. Travel Information:',
    fields: [
      { field: 'overnightRequired', label: 'Room Overnight' },
      { field: 'estimatedTimeOfDeparture', label: 'Estimated Time of Departure' },
      { field: 'actualReleaseDateTime', label: 'Actual Date/Time of Release' },
      { field: 'destination', label: 'Destination' },
      { field: 'estimatedTimeOfArrival', label: 'Estimated Date/Time of Arrival' },
      { field: 'travelMethod', label: 'Travel Method' },
      {
        field: 'contactInformationWhileTraveling',
        label: 'Contact Information While Traveling',
      },
      { field: 'manifestRequired', label: 'Manifest' },
      { field: 'manifestNumber', label: 'Manifest #' },
      { field: 'unitAgencyNotified', label: 'Unit/Agency Notified' },
      { field: 'unitAgencyNotifiedText', label: 'Notified' },
    ],
  },
  {
    kind: 'fields',
    label: '9. Prepared by:',
    fields: [
      { field: 'preparedByName', label: 'Name' },
      { field: 'preparedByPosition', label: 'Position Title' },
      { field: 'preparedDateTime', label: 'Date/Time' },
    ],
  },
];

/** Printed order of the check-off rows in box 6, grouped as the sheet groups them. */
export const ICS221_CHECKOFF_UNITS = [
  { key: 'supply', label: 'Supply Unit' },
  { key: 'communications', label: 'Communications Unit' },
  { key: 'time', label: 'Time Unit' },
  { key: 'safety', label: 'Safety Officer' },
  { key: 'agency_rep', label: 'Agency Representative' },
  { key: 'documentation', label: 'Documentation Unit' },
  { key: 'demobilization', label: 'Demobilization Unit' },
] as const;

/* ICS 238-CG Demob Tracking Table */

export const ICS238_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Date/Time:', field: 'dateTime' },
  {
    kind: 'parallel',
    label: 'Check-in and Demob Information',
    columns: [
      { field: 'agency', label: '4. Agency' },
      { field: 'strikeTeamTaskForce', label: 'ST / TF' },
      { field: 'kind', label: 'Kind' },
      { field: 'type', label: 'Type' },
      { field: 'resourceId', label: 'ID No. / Name' },
      { field: 'orderNumber', label: '5. Order Number' },
      { field: 'checkInDateTime', label: '6. Date/Time Check-in' },
      { field: 'leaderName', label: "7. Leader's Name" },
      { field: 'personnelCount', label: '8. Total No. Pers' },
      { field: 'incidentContactInfo', label: '9. Incident Contact Info' },
      { field: 'incidentLodgingInfo', label: '10. Incident Lodging Info' },
      { field: 'homeUnit', label: '11. Home Unit' },
      { field: 'travelMethod', label: '12. Method of Travel' },
      { field: 'incidentAssignment', label: '13. Incident Assignment' },
      { field: 'otherQualifications', label: '14. Other Quals' },
      { field: 'sentToRestat', label: '15. Sent to RESTAT' },
      { field: 'excess', label: '16. Excess' },
      { field: 'dateLastShift', label: '17. Date Last Shift' },
      { field: 'etaToHome', label: '18. ETA to Home (hrs)' },
      { field: 'dateSentHome', label: '19. Date Sent Home' },
      { field: 'dateArrivedHome', label: '20. Date Arrived Home' },
    ],
  },
];

/* ICS 230-CG Daily Meeting Schedule */

export const ICS230_BOXES: readonly IcsDocumentBox[] = [
  { kind: 'value', label: '1. Incident Name:', field: 'incidentName' },
  { kind: 'value', label: '2. Incident Location:', field: 'incidentLocation' },
  {
    kind: 'fields',
    label: '3. Operational Period (Date/Time):',
    fields: [
      { field: 'opFrom', label: 'From' },
      { field: 'opTo', label: 'To' },
    ],
  },
  {
    kind: 'rows',
    label: '4. Meeting Schedule (Commonly held meetings are included):',
    field: 'meetings',
    columns: [
      { field: 'dateTime', label: 'Date / Time' },
      { field: 'meetingName', label: 'Meeting Name' },
      { field: 'purpose', label: 'Purpose' },
      { field: 'attendees', label: 'Attendees' },
      { field: 'location', label: 'Location' },
    ],
  },
  {
    kind: 'fields',
    label: '5. Prepared by:',
    fields: [
      { field: 'preparedBy', label: 'Name' },
      { field: 'positionTitle', label: 'Position Title' },
    ],
  },
];
